import { Components } from 'formiojs';
import FieldComponent from 'formiojs/components/_classes/field/Field';

export class DocumentComponent extends FieldComponent {
  declare component: any;
  declare refs: {
    fileInput?: HTMLInputElement;
    dropdown?: HTMLSelectElement;
    tagInput?: HTMLInputElement;
    tagList?: HTMLUListElement;
    createdByInput?: HTMLInputElement;
    updatedByInput?: HTMLInputElement;
  };
  
  private tags: string[] = [];
  declare dataValue: any;
  addEventListener: any;

  static schema(...extend: any[]) {
    return FieldComponent.schema(
      {
        type: 'document',
        label: 'Document Upload',
        key: 'document',
        input: true,
        showDocumentType: false,   // checkbox
        documentOptions: [],       // dynamic options
        showTagInput: false,       // checkbox for tag input
        showCreatedBy: false,      // checkbox for created by
        showUpdatedBy: false       // checkbox for updated by
      },
      ...extend
    );
  }

  static get builderInfo() {
    return {
      title: 'Document',
      group: 'basic',
      icon: 'file',
      weight: 80,
      schema: DocumentComponent.schema()
    };
  }

  // ✅ Extend default edit panel + add our fields
  static editForm = () => {
    const editForm = FieldComponent.editForm();

    // Insert "Show Document Type"
    editForm.components.push({
      type: 'checkbox',
      label: 'Show Document Type',
      key: 'showDocumentType',
      input: true,
      weight: 50
    });

    // Insert "Document Options" datagrid
    editForm.components.push({
      type: 'datagrid',
      label: 'Document Options',
      key: 'documentOptions',
      input: true,
      addAnother: 'Add Option',
      weight: 60,
      components: [
        {
          type: 'textfield',
          key: 'label',
          label: 'Label',
          input: true
        },
        {
          type: 'textfield',
          key: 'value',
          label: 'Value',
          input: true
        }
      ],
      conditional: {
        json: { '===': [{ var: 'data.showDocumentType' }, true] }
      }
    });

    // Insert "Show Tag Input" checkbox
    editForm.components.push({
      type: 'checkbox',
      label: 'Show Tag Input',
      key: 'showTagInput',
      input: true,
      weight: 70
    });

    // Insert "Show Created By" checkbox
    editForm.components.push({
      type: 'checkbox',
      label: 'Show Created By',
      key: 'showCreatedBy',
      input: true,
      weight: 80
    });

    // Insert "Show Updated By" checkbox
    editForm.components.push({
      type: 'checkbox',
      label: 'Show Updated By',
      key: 'showUpdatedBy',
      input: true,
      weight: 90
    });

    return editForm;
  };

  // Render UI
  render(children: any) {
    const showDropdown = this.component.showDocumentType;
    const showTagInput = this.component.showTagInput;
    const showCreatedBy = this.component.showCreatedBy;
    const showUpdatedBy = this.component.showUpdatedBy;
    let dropdownHtml = '';
    let tagInputHtml = '';
    let createdByHtml = '';
    let updatedByHtml = '';

    if (showDropdown && Array.isArray(this.component.documentOptions)) {
      dropdownHtml = `
        <select ref="dropdown">
          <option value="">Select Type</option>
          ${this.component.documentOptions
            .map(
              (opt: any) =>
                `<option value="${opt.value}">${opt.label}</option>`
            )
            .join('')}
        </select>
      `;
    }

    if (showTagInput) {
      tagInputHtml = `
        <div class="tag-input-container" style="margin-top: 10px;">
          <label>Tags:</label>
          <ul ref="tagList" class="tags" style="list-style: none; padding: 0; margin: 5px 0; display: flex; flex-wrap: wrap; gap: 5px;"></ul>
          <input ref="tagInput" type="text" class="tag-input-field" placeholder="Add a tag and press Enter..." style="width: 100%; padding: 5px; border: 1px solid #ccc; border-radius: 4px;" />
        </div>
      `;
    }

    if (showCreatedBy || showUpdatedBy) {
      const createdByField = showCreatedBy ? `
        <div style="flex: 1; margin-right: ${showUpdatedBy ? '10px' : '0'};">
          <label>Created By:</label>
          <input ref="createdByInput" type="text" placeholder="Enter creator name..." style="width: 100%; padding: 5px; border: 1px solid #ccc; border-radius: 4px;" />
        </div>
      ` : '';
      
      const updatedByField = showUpdatedBy ? `
        <div style="flex: 1;">
          <label>Updated By:</label>
          <input ref="updatedByInput" type="text" placeholder="Enter updater name..." style="width: 100%; padding: 5px; border: 1px solid #ccc; border-radius: 4px;" />
        </div>
      ` : '';
      
      createdByHtml = `
        <div class="user-fields-container" style="margin-top: 10px; display: flex; gap: 10px;">
          ${createdByField}
          ${updatedByField}
        </div>
      `;
    }

    return super.render(`
      <div>
        <input ref="fileInput" type="file" />
        ${dropdownHtml}
        ${tagInputHtml}
        ${createdByHtml}
      </div>
    `);
  }

  attach(element: any) {
    const refs = super.attach(element);

    // Handle file input
    if (this.refs.fileInput) {
      this.addEventListener(this.refs.fileInput, 'change', (event: any) => {
        const file = event.target.files[0];
        if (file) {
          const reader = new FileReader();
          reader.onload = () => {
            this.setValue({
              fileName: file.name,
              fileData: reader.result, // base64
              fileType: this.refs.dropdown?.value || null,
              tags: this.tags
            });
          };
          reader.readAsDataURL(file);
        }
      });
    }

    // Handle dropdown
    if (this.refs.dropdown) {
      this.addEventListener(this.refs.dropdown, 'change', () => {
        const currentValue = this.dataValue || {};
        this.setValue({
          ...currentValue,
          fileType: this.refs.dropdown?.value || null
        });
      });
    }

    // Handle tag input with timeout to ensure DOM is ready
    setTimeout(() => {
      const tagInput = element.querySelector('.tag-input-field') as HTMLInputElement;
      const tagList = element.querySelector('.tags') as HTMLUListElement;
      
      if (tagInput && tagList) {
        const renderTags = () => {
          tagList.innerHTML = this.tags
            .map(
              (tag, i) => `
              <li class="tag" style="background: #007bff; color: white; padding: 2px 8px; border-radius: 12px; font-size: 12px; display: flex; align-items: center; gap: 5px;">
                <span>${tag}</span>
                <i class="remove" data-index="${i}" style="cursor: pointer; font-weight: bold;">&times;</i>
              </li>`
            )
            .join('');
        };

        const addTag = (value: string) => {
          value = value.trim();
          if (value && !this.tags.includes(value)) {
            this.tags.push(value);
            const currentValue = this.dataValue || {};
            this.setValue({
              ...currentValue,
              tags: this.tags
            });
            renderTags();
          }
          tagInput.value = '';
        };

        const removeTag = (index: number) => {
          this.tags.splice(index, 1);
          const currentValue = this.dataValue || {};
          this.setValue({
            ...currentValue,
            tags: this.tags
          });
          renderTags();
        };

        tagInput.addEventListener('keydown', (e: KeyboardEvent) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            addTag(tagInput.value);
          }
        });

        tagInput.addEventListener('input', (e: Event) => {
          const input = e.target as HTMLInputElement;
          const value = input.value;
          if (value.includes(',')) {
            const tags = value.split(',');
            const lastTag = tags.pop() || '';
            tags.forEach(tag => {
              if (tag.trim()) addTag(tag);
            });
            input.value = lastTag;
          }
        });

        tagList.addEventListener('click', (e: MouseEvent) => {
          const target = e.target as HTMLElement;
          if (target.classList.contains('remove')) {
            const index = parseInt(target.dataset['index'] || '0', 10);
            removeTag(index);
          }
        });

        // Initial render
        renderTags();
      }
    }, 100);

    // Handle created by input
    const createdByInput = element.querySelector('[ref="createdByInput"]') as HTMLInputElement;
    if (createdByInput) {
      createdByInput.addEventListener('input', () => {
        const currentValue = this.dataValue || {};
        this.setValue({
          ...currentValue,
          createdBy: createdByInput.value
        });
      });
    }

    // Handle updated by input
    const updatedByInput = element.querySelector('[ref="updatedByInput"]') as HTMLInputElement;
    if (updatedByInput) {
      updatedByInput.addEventListener('input', () => {
        const currentValue = this.dataValue || {};
        this.setValue({
          ...currentValue,
          updatedBy: updatedByInput.value
        });
      });
    }

    return refs;
  }

  getValue() {
    return this.dataValue || null;
  }

  setValue(value: any) {
    this.dataValue = value;
    if (value && value.tags && Array.isArray(value.tags)) {
      this.tags = value.tags;
    }
    return true;
  }
}

// Register custom component
(Components as any).addComponent('document', DocumentComponent);
